import { useEffect, useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";

/**
 * Refus d'un article par la rédaction : demande de révision (l'auteur corrige
 * et resoumet) ou rejet définitif (article archivé). Le commentaire est
 * obligatoire — il est transmis à l'auteur (email, ou webhook pour la
 * newsroom IA).
 */
export function RefusArticleDialog({
  titreArticle,
  open,
  onOpenChange,
  onConfirm,
  loading,
}: {
  titreArticle?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (commentaire: string, definitif: boolean) => void;
  loading?: boolean;
}) {
  const { t } = useI18n();
  const [commentaire, setCommentaire] = useState("");
  const [definitif, setDefinitif] = useState(false);

  useEffect(() => {
    if (open) {
      setCommentaire("");
      setDefinitif(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Refuser « {titreArticle} »</DialogTitle>
          <DialogDescription>
            Ce commentaire est obligatoire : il est transmis au contributeur pour qu'il puisse corriger son article
            et le resoumettre.
          </DialogDescription>
        </DialogHeader>
        <textarea
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
          placeholder="Ex. : Merci de citer vos sources et de préciser la date des faits…"
          rows={4}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
        />
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={definitif}
            onChange={(e) => setDefinitif(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border"
          />
          <span>
            <span className="font-medium">Rejet définitif</span>
            <span className="block text-xs text-muted-foreground">
              L'article est archivé : aucune nouvelle soumission n'est attendue.
            </span>
          </span>
        </label>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-border px-4 py-2 text-sm"
          >
            {t("admin.common.cancel")}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(commentaire.trim(), definitif)}
            disabled={!commentaire.trim() || loading}
            className="inline-flex items-center gap-2 rounded-md bg-alert px-4 py-2 text-sm font-semibold text-alert-foreground disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {definitif ? "Rejeter définitivement" : "Demander une révision"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Proposé quand la validation est bloquée parce qu'un droit de réponse requis
 * est encore en attente : l'admin peut publier quand même, en dérogation
 * (tracée dans le journal d'audit de l'article).
 */
export function DerogationDroitReponseDialog({
  open,
  onOpenChange,
  onConfirm,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  loading?: boolean;
}) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-alert" /> Droit de réponse en attente
          </DialogTitle>
          <DialogDescription>
            La fiche de vérification indique qu'un droit de réponse est requis et n'a pas encore été obtenu.
            Mettez à jour la fiche une fois la réponse reçue, ou publiez en dérogation : votre décision sera
            enregistrée dans l'historique de l'article.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-border px-4 py-2 text-sm"
          >
            {t("admin.common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md bg-alert px-4 py-2 text-sm font-semibold text-alert-foreground disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Publier en dérogation
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
